import React, { useRef, useState } from 'react'
import { useSession } from 'src/sdk/session'
import { useLocation } from '@reach/router'
import { useVideoEvent } from 'src/sdk/analytics/hooks/useVideoEvent'
import Lazyload from 'react-lazy-load'
import { isNewPDP } from 'src/utils/isNewPDP'
import { getCookie } from 'src/utils/cookie'

interface Props {
  poster: string
  videoSrcURL: string
  videoTitle: string
  controlsVideo: boolean
  soundVideo: boolean
  loopingVideo?: boolean
  videoId?: string
  isTrackingDatalayer?: boolean
}

const ProductVideo = ({
  poster,
  videoSrcURL,
  videoTitle,
  controlsVideo,
  soundVideo,
  loopingVideo,
  videoId,
  isTrackingDatalayer,
}: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [hasStarted, setHasStarted] = useState<boolean>(false)
  const { person } = useSession()
  const { href } = useLocation()
  const { sendVideoEvent } = useVideoEvent()
  const uuid = getCookie('uuid')
  const percentagesToTrack = [10, 25, 50, 75, 100]
  const [sentPercentages, setSentPercentages] = useState<Set<number>>(new Set())

  const isFullscreen =
    document.fullscreenElement === videoRef.current ||
    (document as any).webkitFullscreenElement === videoRef.current ||
    (document as any).mozFullScreenElement === videoRef.current ||
    (document as any).msFullscreenElement === videoRef.current

  const handlePlay = () => {
    const video = videoRef.current

    if (!video || hasStarted) {
      return
    }

    sendVideoEvent({
      event: 'video_start',
      pdp_version: isNewPDP ? 'versao_2' : 'versao_1',
      video_progress: '0%',
      fullScreen: isFullscreen,
      muted: video.muted,
      video_complete: false,
      user_id: person?.id ?? null,
      user_logged: !!person?.id,
      pageType: href,
      uuid,
    })

    setHasStarted(true)
  }

  const handlePause = () => {
    const video = videoRef.current

    if (!video || video.duration === 0) {
      return
    }

    const currentPercent = (video.currentTime / video.duration) * 100
    const newSent = new Set(sentPercentages)

    percentagesToTrack.forEach((p) => {
      if (currentPercent >= p && !newSent.has(p)) {
        sendVideoEvent({
          event: p === 100 ? 'view_complete' : 'video_progress',
          pdp_version: isNewPDP ? 'versao_2' : 'versao_1',
          video_progress: `${p}%`,
          fullScreen: isFullscreen,
          muted: video.muted,
          video_complete: p === 100,
          user_id: person?.id ?? null,
          user_logged: !!person?.id,
          pageType: href,
          uuid,
        })

        newSent.add(p)
      }
    })

    setSentPercentages(newSent)

    if (currentPercent >= 100) {
      setHasStarted(false)
      setSentPercentages(new Set())
    }
  }

  const handleEnded = () => {
    setHasStarted(false)
    setSentPercentages(new Set())
  }

  return (
    <div>
      <Lazyload>
        <video
          ref={videoRef}
          id={videoId}
          src={videoSrcURL}
          poster={poster}
          controls={controlsVideo}
          autoPlay={false}
          muted={!soundVideo}
          loop={!isTrackingDatalayer && !!loopingVideo}
          playsInline
          preload="metadata"
          title={videoTitle}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
        />
      </Lazyload>
    </div>
  )
}

export default ProductVideo
